import {
  DatabaseSequenceSchema,
  GraphNodeTypeSchema,
  OpaqueCursorSchema,
  UuidSchema,
  type GraphNodeType,
} from "@mind/contracts/browser";

export type GraphView = "map" | "list";

export type GraphSearch = {
  readonly workspace?: string;
  readonly rootType?: GraphNodeType;
  readonly rootId?: string;
  readonly rootRevision?: string;
  readonly project?: string;
  readonly account?: string;
  readonly tag?: string;
  readonly includeArchived?: boolean;
  readonly cursor?: string;
  readonly view?: GraphView;
};

const GRAPH_SEARCH_KEYS: Readonly<Record<string, true>> = {
  workspace: true,
  rootType: true,
  rootId: true,
  rootRevision: true,
  project: true,
  account: true,
  tag: true,
  includeArchived: true,
  cursor: true,
  view: true,
};

export function validateGraphSearch(input: unknown): GraphSearch {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    throw new TypeError("Graph route search must be an object");
  }
  const search = input as Readonly<Record<string, unknown>>;
  const unexpectedKey = Object.keys(search).find((key) => GRAPH_SEARCH_KEYS[key] !== true);
  if (unexpectedKey !== undefined) throw new TypeError(`Unexpected Graph route search key: ${unexpectedKey}`);

  const workspace = parseOptionalUuid(search["workspace"], "workspace");
  const rootType = parseOptionalNodeType(search["rootType"]);
  const rootId = parseOptionalUuid(search["rootId"], "rootId");
  const rootRevision = parseOptionalSequence(search["rootRevision"], "rootRevision");
  const rootFields = [rootType, rootId, rootRevision];
  if (rootFields.some((value) => value !== undefined) && rootFields.some((value) => value === undefined)) {
    throw new TypeError("Graph rootType, rootId, and rootRevision must be provided together");
  }
  const project = parseOptionalUuid(search["project"], "project");
  const account = parseOptionalUuid(search["account"], "account");
  const tag = parseOptionalTag(search["tag"]);
  const includeArchived = parseOptionalBoolean(search["includeArchived"], "includeArchived");
  const cursor = parseOptionalCursor(search["cursor"]);
  const view = parseOptionalView(search["view"]);
  if (cursor !== undefined && rootId === undefined) throw new TypeError("Graph cursor requires a root");

  return {
    ...(workspace === undefined ? {} : { workspace }),
    ...(rootType === undefined ? {} : { rootType }),
    ...(rootId === undefined ? {} : { rootId }),
    ...(rootRevision === undefined ? {} : { rootRevision }),
    ...(project === undefined ? {} : { project }),
    ...(account === undefined ? {} : { account }),
    ...(tag === undefined ? {} : { tag }),
    ...(includeArchived === undefined ? {} : { includeArchived }),
    ...(cursor === undefined ? {} : { cursor }),
    ...(view === undefined ? {} : { view }),
  };
}

export function stripGraphSearch(input: Readonly<Record<string, unknown>>): GraphSearch {
  const allowed: Record<string, unknown> = {};
  for (const key of Object.keys(GRAPH_SEARCH_KEYS)) {
    if (input[key] !== undefined && input[key] !== null && input[key] !== "") allowed[key] = input[key];
  }
  return validateGraphSearch(allowed);
}

export function withGraphRoot(
  current: GraphSearch,
  root: { readonly type: GraphNodeType; readonly id: string; readonly revision: string },
): GraphSearch {
  return validateGraphSearch({
    ...current,
    rootType: root.type,
    rootId: root.id,
    rootRevision: root.revision,
    cursor: undefined,
  });
}

export function withGraphCursor(current: GraphSearch, cursor: string | null): GraphSearch {
  return validateGraphSearch({ ...current, cursor: cursor ?? undefined });
}

export function withGraphView(current: GraphSearch, view: GraphView): GraphSearch {
  return validateGraphSearch({ ...current, view });
}

function parseOptionalUuid(value: unknown, key: string): string | undefined {
  if (value === undefined) return undefined;
  const parsed = UuidSchema.safeParse(value);
  if (!parsed.success) throw new TypeError(`Invalid Graph ${key}`);
  return parsed.data;
}

function parseOptionalNodeType(value: unknown): GraphNodeType | undefined {
  if (value === undefined) return undefined;
  const parsed = GraphNodeTypeSchema.safeParse(value);
  if (!parsed.success) throw new TypeError("Invalid Graph rootType");
  return parsed.data;
}

function parseOptionalSequence(value: unknown, key: string): string | undefined {
  if (value === undefined) return undefined;
  const parsed = DatabaseSequenceSchema.safeParse(value);
  if (!parsed.success) throw new TypeError(`Invalid Graph ${key}`);
  return parsed.data;
}

function parseOptionalCursor(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  const parsed = OpaqueCursorSchema.safeParse(value);
  if (!parsed.success) throw new TypeError("Invalid Graph cursor");
  return parsed.data;
}

function parseOptionalTag(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string" || value.trim().length > 100) throw new TypeError("Invalid Graph tag");
  const tag = value.trim();
  return tag.length === 0 ? undefined : tag;
}

function parseOptionalBoolean(value: unknown, key: string): boolean | undefined {
  if (value === undefined) return undefined;
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  throw new TypeError(`Invalid Graph ${key}`);
}

function parseOptionalView(value: unknown): GraphView | undefined {
  if (value === undefined) return undefined;
  if (value === "map" || value === "list") return value;
  throw new TypeError("Invalid Graph view");
}
